import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  WxbAlert,
  WxbButton,
  WxbDivider,
  WxbEmpty,
  WxbIcon,
  WxbInput,
  WxbPageHeader,
  WxbPageSection,
  WxbPageShell,
  WxbPageToolbar,
  WxbSelect,
  WxbSpinner,
  WxbStatusBadge,
  WxbTag,
  WxbTextarea,
  wxbToast,
} from '../components/wxb-ui';
import CipLoopRelationshipSvg from '../components/ProdCipLoops/CipLoopRelationshipSvg';
import { parseFacilityCodes } from '../components/ProdResourceTopology/resourceTopologyModel';
import { prodCipLoopApi } from '../services/prodCipLoopApi';
import { normalizeResourceTopologyError, prodResourceTopologyApi } from '../services/prodResourceTopologyApi';
import type { CipLoop, CipLoopDraft, CipLoopWorkspace } from '../types/prodCipLoop';
import type { ResourceNode, ResourceTopologyHistoryItem } from '../types/prodResourceTopology';
import './ProdCipLoopsPage.css';

const FACILITY_CODES = parseFacilityCodes(process.env.REACT_APP_PROD_RESOURCE_FACILITY_CODES);
const emptyDraft = (): CipLoopDraft => ({
  name: '',
  lifecycleStatus: 'ACTIVE',
  note: '',
  primaryStationResourceId: '',
  memberResourceIds: [],
  backupStationResourceIds: [],
});

const resourceLabel = (resource: ResourceNode | undefined, fallback: string) => resource ? `${resource.code} · ${resource.name}` : fallback;
const VERSION_STATUS_LABEL: Record<string, string> = { DRAFT: '草稿', VALIDATED: '已校验', PUBLISHED: '已发布' };

export default function ProdCipLoopsPage() {
  const [facilityCode, setFacilityCode] = useState(FACILITY_CODES[0]);
  const [versions, setVersions] = useState<ResourceTopologyHistoryItem[]>([]);
  const [workspace, setWorkspace] = useState<CipLoopWorkspace | null>(null);
  const [selectedStationResourceId, setSelectedStationResourceId] = useState<string | null>(null);
  const [selectedLoopId, setSelectedLoopId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState<CipLoopDraft>(emptyDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedLoop = workspace?.loops.find((loop) => loop.cipLoopId === selectedLoopId) ?? null;
  const writable = workspace?.version.status === 'DRAFT' || workspace?.version.status === 'VALIDATED';
  const stations = useMemo(() => workspace?.resources.filter((resource) => resource.kind === 'CIP_STATION' && resource.lifecycleStatus !== 'RETIRED') ?? [], [workspace?.resources]);
  const candidates = useMemo(() => workspace?.resources.filter((resource) => (
    ['EQUIPMENT', 'COMPONENT'].includes(resource.kind)
      && resource.lifecycleStatus !== 'RETIRED'
      && resource.attributes?.cipCleanable === true
  )) ?? [], [workspace?.resources]);
  const selectedStation = stations.find((station) => station.id === selectedStationResourceId) ?? null;
  const stationLoops = useMemo(() => workspace?.loops.filter((loop) => loop.primaryStationResourceId === selectedStationResourceId) ?? [], [selectedStationResourceId, workspace?.loops]);

  const selectLoop = useCallback((loop: CipLoop) => {
    setSelectedLoopId(loop.cipLoopId);
    setSelectedStationResourceId(loop.primaryStationResourceId);
    setDraft({
      name: loop.name,
      lifecycleStatus: loop.lifecycleStatus === 'RETIRED' ? 'INACTIVE' : loop.lifecycleStatus,
      note: loop.note ?? '',
      primaryStationResourceId: loop.primaryStationResourceId ?? '',
      memberResourceIds: loop.memberResourceIds,
      backupStationResourceIds: loop.backupStationResourceIds,
    });
    setEditorOpen(true);
  }, []);

  const startCreate = useCallback((stationResourceId?: string) => {
    const primaryStationResourceId = stationResourceId ?? selectedStationResourceId ?? stations[0]?.id ?? '';
    setSelectedLoopId(null);
    setSelectedStationResourceId(primaryStationResourceId || null);
    setDraft({ ...emptyDraft(), primaryStationResourceId });
    setEditorOpen(true);
  }, [selectedStationResourceId, stations]);

  const selectStation = useCallback((stationResourceId: string) => {
    setSelectedStationResourceId(stationResourceId);
    const nextLoop = workspace?.loops.find((loop) => loop.primaryStationResourceId === stationResourceId);
    if (nextLoop) {
      selectLoop(nextLoop);
      return;
    }
    setSelectedLoopId(null);
    setDraft({ ...emptyDraft(), primaryStationResourceId: stationResourceId });
    setEditorOpen(false);
  }, [selectLoop, workspace?.loops]);

  const load = useCallback(async (requestedVersionId?: string) => {
    setLoading(true);
    setError(null);
    try {
      const history = await prodResourceTopologyApi.listVersions(facilityCode);
      setVersions(history);
      const version = (requestedVersionId ? history.find((item) => item.id === requestedVersionId) : null)
        ?? history.find((item) => item.status === 'DRAFT' || item.status === 'VALIDATED')
        ?? history.find((item) => item.status === 'PUBLISHED')
        ?? null;
      if (!version) {
        setWorkspace(null);
        setSelectedStationResourceId(null);
        setSelectedLoopId(null);
        setEditorOpen(false);
        setDraft(emptyDraft());
        return;
      }
      const next = await prodCipLoopApi.getWorkspace(version.id);
      setWorkspace(next);
      const firstLoop = next.loops[0];
      const firstStation = next.resources.find((resource) => resource.kind === 'CIP_STATION' && resource.lifecycleStatus !== 'RETIRED');
      if (firstLoop) {
        selectLoop(firstLoop);
      } else {
        setSelectedStationResourceId(firstStation?.id ?? null);
        setSelectedLoopId(null);
        setDraft({ ...emptyDraft(), primaryStationResourceId: firstStation?.id ?? '' });
        setEditorOpen(false);
      }
    } catch (requestError) {
      setError(normalizeResourceTopologyError(requestError).message);
    } finally {
      setLoading(false);
    }
  }, [facilityCode, selectLoop]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!workspace) return;
    if (!draft.name.trim() || !draft.primaryStationResourceId) {
      setError('请填写回路名称并选择主 CIP 站。');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = selectedLoop
        ? await prodCipLoopApi.update(workspace.version.id, selectedLoop.cipLoopId, workspace.version.revision, draft)
        : await prodCipLoopApi.create(workspace.version.id, workspace.version.revision, draft);
      const next = await prodCipLoopApi.getWorkspace(result.version.id);
      setWorkspace(next);
      const nextLoop = next.loops.find((loop) => loop.cipLoopId === result.cipLoop.cipLoopId);
      if (nextLoop) selectLoop(nextLoop);
      wxbToast.success(selectedLoop ? 'CIP 逻辑回路已保存' : 'CIP 逻辑回路已创建');
    } catch (requestError) {
      setError(normalizeResourceTopologyError(requestError).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <WxbPageShell className="cip-loops-page" size="wide" gap="lg">
      <WxbPageHeader
        eyebrow="生产排产 数据底座 · 模块 1"
        title="CIP 逻辑回路"
        description="从 CIP 站出发，维护其服务回路与可清洗成员。逻辑回路不进入物理资源树。"
        meta={workspace ? <WxbStatusBadge tone={workspace.version.status === 'PUBLISHED' ? 'success' : workspace.version.status === 'VALIDATED' ? 'info' : 'warning'} label={`拓扑 V${workspace.version.versionNo} · ${VERSION_STATUS_LABEL[workspace.version.status] ?? workspace.version.status}`} size="sm" /> : undefined}
        actions={<WxbButton type="button" disabled={!writable || !stations.length} onClick={() => startCreate()}>新建回路</WxbButton>}
      />
      <WxbPageToolbar
        className="cip-loops-toolbar"
        filters={<><WxbSelect label="设施" value={facilityCode} options={FACILITY_CODES.map((code) => ({ value: code, label: code }))} onChange={setFacilityCode} /><WxbSelect label="拓扑版本" value={workspace?.version.id} options={versions.map((version) => ({ value: version.id, label: `V${version.versionNo} · ${version.name} · ${VERSION_STATUS_LABEL[version.status] ?? version.status}` }))} onChange={(versionId) => load(versionId)} /></>}
        summary={workspace ? <span>{stations.length} 个 CIP 站 · {workspace.loops.length} 条逻辑回路 · {candidates.length} 个可清洗成员</span> : '选择拓扑版本后显示回路配置'}
      />
      {error && <WxbAlert variant="error" title="操作未完成">{error}</WxbAlert>}
      {loading ? <div className="cip-loops-loading"><WxbSpinner tip="加载 CIP 逻辑回路…" /></div> : !workspace ? <WxbPageSection variant="framed"><WxbEmpty description="当前设施还没有资源拓扑版本，请先维护物理资源。" /></WxbPageSection> : (
        <>
          {!writable && <WxbAlert title="当前为只读拓扑版本">请从已发布版本创建草稿后，再维护 CIP 逻辑回路。</WxbAlert>}
          {!stations.length ? <WxbPageSection variant="framed" title="尚未发现 CIP 站" description="逻辑回路由 CIP 站服务。请先在“资源与物理拓扑”中维护并启用至少一个 CIP 站。"><WxbEmpty description="没有可用于建立逻辑回路的 CIP 站。" /></WxbPageSection> : (
            <div className="cip-loops-workbench">
              <WxbPageSection className="cip-loops-navigation" variant="framed" density="compact" title="CIP 服务网络" description="站是服务主体；回路是站下的逻辑能力。">
                <div className="cip-loops-station-list">
                  {stations.map((station) => {
                    const loopCount = workspace.loops.filter((loop) => loop.primaryStationResourceId === station.id).length;
                    const selected = station.id === selectedStationResourceId;
                    return (
                      <WxbButton key={station.id} type="button" variant="ghost" className={`cip-loops-station-item${selected ? ' is-selected' : ''}`} onClick={() => selectStation(station.id)}>
                        <span className="cip-loops-station-item-head"><span className="cip-loops-station-icon"><WxbIcon name="cip-system" size={18} /></span><span className="cip-loops-station-item-copy"><strong>{station.name}</strong><code>{station.code}</code></span></span>
                        <span className="cip-loops-station-item-count">管理 {loopCount} 条回路</span>
                      </WxbButton>
                    );
                  })}
                </div>
                <WxbDivider />
                <div className="cip-loops-network-branch">
                  <div className="cip-loops-network-branch-header">
                    <div><strong>{selectedStation ? `${selectedStation.name} 的回路` : '服务回路'}</strong><span>{selectedStation ? '选择一条回路，在右侧完成关系与配置。' : '请先选择 CIP 站。'}</span></div>
                    <WxbButton type="button" size="sm" variant="secondary" disabled={!writable || !selectedStation} onClick={() => startCreate(selectedStation?.id)}>新建</WxbButton>
                  </div>
                  {stationLoops.length ? <div className="cip-loops-loop-list">
                    {stationLoops.map((loop) => {
                      const selected = loop.cipLoopId === selectedLoopId;
                      return (
                        <WxbButton key={loop.cipLoopId} type="button" variant="ghost" className={`cip-loops-loop-item${selected ? ' is-selected' : ''}`} onClick={() => selectLoop(loop)}>
                          <span className="cip-loops-loop-item-head"><span className="cip-loops-loop-item-copy"><strong>{loop.name}</strong><code>{loop.code}</code></span><WxbStatusBadge tone={loop.lifecycleStatus === 'ACTIVE' ? 'success' : 'neutral'} label={loop.lifecycleStatus === 'ACTIVE' ? '启用' : '停用'} size="sm" /></span>
                          <span className="cip-loops-loop-item-meta"><span>{loop.memberResourceIds.length} 个成员</span><span>{loop.backupStationResourceIds.length} 个备站</span></span>
                        </WxbButton>
                      );
                    })}
                  </div> : <WxbEmpty className="cip-loops-list-empty" description={selectedStation ? '该 CIP 站还没有服务回路。' : '选择 CIP 站后查看其服务回路。'} />}
                </div>
              </WxbPageSection>

              <WxbPageSection
                className="cip-loops-detail"
                variant="framed"
                density="compact"
                title={selectedLoop ? selectedLoop.name : selectedStation ? `${selectedStation.name} 的服务回路` : '回路工作区'}
                description={selectedLoop ? '关系图和配置在同一工作区维护。' : selectedStation ? '先建立一条逻辑回路，再把可清洗设备或组件纳入服务范围。' : '选择一个 CIP 站后开始维护。'}
                actions={editorOpen ? <div className="cip-loops-detail-actions">{selectedLoop && <WxbTag color="cyan">{selectedLoop.code}</WxbTag>}<WxbStatusBadge tone={draft.lifecycleStatus === 'ACTIVE' ? 'success' : 'neutral'} label={draft.lifecycleStatus === 'ACTIVE' ? '启用' : '停用'} size="sm" /><WxbButton type="button" size="sm" disabled={!writable || saving} onClick={save}>{saving ? '保存中…' : selectedLoop ? '保存回路' : '创建回路'}</WxbButton></div> : undefined}
              >
                <WxbPageSection className="cip-loops-relationship-zone" density="compact" title="1. 服务关系" description="关系图只概览服务归属；成员与备站以固定数量摘要展示，完整明细在下方维护。">
                  <CipLoopRelationshipSvg loop={editorOpen ? selectedLoop : null} resources={workspace.resources} focusStation={selectedStation} />
                </WxbPageSection>
                <WxbDivider />
                <WxbPageSection className="cip-loops-configuration-zone" density="compact" title={editorOpen ? '2. 回路配置' : '2. 建立第一条回路'} description={editorOpen ? '按业务顺序维护信息、服务路由与清洗成员。' : '创建后系统会生成稳定编码，并在此展示实际服务关系。'}>
                  {editorOpen ? <div className="cip-loops-editor-stack">
                    <section className="cip-loops-form-step">
                      <div className="cip-loops-form-step-heading"><h3>基础信息</h3><p>名称面向业务人员；编码在首次保存时由系统生成。</p></div>
                    <div className="cip-loops-form-grid cip-loops-form-grid-basic">
                      <WxbInput label="回路名称" value={draft.name} disabled={!writable} placeholder="例如 上游纯化 CIP 回路" onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} />
                      <WxbSelect label="生命周期" value={draft.lifecycleStatus} disabled={!writable} options={[{ value: 'ACTIVE', label: '启用' }, { value: 'INACTIVE', label: '停用' }]} onChange={(value) => setDraft((current) => ({ ...current, lifecycleStatus: value as CipLoopDraft['lifecycleStatus'] }))} />
                    </div>
                    </section>
                    <WxbDivider />
                    <section className="cip-loops-form-step">
                      <div className="cip-loops-form-step-heading"><h3>服务站与备站</h3><p>主 CIP 站承担正常排产；备站仅供人工应急指定。</p></div>
                    <div className="cip-loops-form-grid">
                      <WxbSelect label="主 CIP 站" value={draft.primaryStationResourceId || undefined} disabled={!writable} placeholder="选择承担正常排产任务的站" options={stations.map((station) => ({ value: station.id, label: resourceLabel(station, station.id) }))} onChange={(value) => { setSelectedStationResourceId(value); setDraft((current) => ({ ...current, primaryStationResourceId: value })); }} />
                      <WxbSelect mode="multiple" label="备 CIP 站" value={draft.backupStationResourceIds.map((item) => item.resourceId)} disabled={!writable} placeholder="可选：仅人工应急使用" options={stations.filter((station) => station.id !== draft.primaryStationResourceId).map((station) => ({ value: station.id, label: resourceLabel(station, station.id) }))} onChange={(values) => setDraft((current) => ({ ...current, backupStationResourceIds: (values as string[]).map((resourceId, index) => ({ resourceId, priority: index + 1 })) }))} />
                    </div>
                    </section>
                    <WxbDivider />
                    <section className="cip-loops-form-step">
                      <div className="cip-loops-form-step-heading"><h3>清洗成员与说明</h3><p>只可加入物理拓扑中已标记为“纳入 CIP 清洗拓扑”的设备或组件。</p></div>
                    <div className="cip-loops-form-grid">
                      <WxbSelect className="cip-loops-members-field" mode="multiple" maxTagCount={2} maxTagPlaceholder={(omitted) => `+${omitted.length} 个成员`} label="可清洗成员" value={draft.memberResourceIds} disabled={!writable} placeholder="选择设备或组件" options={candidates.map((candidate) => ({ value: candidate.id, label: resourceLabel(candidate, candidate.id) }))} onChange={(values) => setDraft((current) => ({ ...current, memberResourceIds: values as string[] }))} />
                      <WxbTextarea className="cip-loops-note" label="说明" value={draft.note} disabled={!writable} rows={3} placeholder="记录回路适用范围或例外说明" onChange={(event) => setDraft((current) => ({ ...current, note: event.target.value }))} />
                    </div>
                    </section>
                    <div className="cip-loops-editor-actions"><WxbButton type="button" variant="ghost" disabled={saving} onClick={() => { setEditorOpen(false); setSelectedLoopId(null); }}>返回关系预览</WxbButton></div>
                  </div> : <WxbEmpty className="cip-loops-preview-empty" description={selectedStation ? `${selectedStation.name} 还没有选中的服务回路。` : '请选择一个 CIP 站，查看其服务关系。'} action={selectedStation && writable ? <WxbButton type="button" onClick={() => startCreate(selectedStation.id)}>创建第一条回路</WxbButton> : undefined} />}
                </WxbPageSection>
              </WxbPageSection>
            </div>
          )}
        </>
      )}
    </WxbPageShell>
  );
}
